import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, isImpersonating, endImpersonation, getImpersonationType, getImpersonatedTenantName, getImpersonatedUserName, getDelegatedUserName } from '@/auth';
import { useUnreadCount, useProfilePicture, useMyAvatarColor, useBrandingLogos } from '@/api';
import { useTheme } from '@/theme';
import { useTranslation } from '@/i18n';
import { useTenant } from '@/tenant';
import { useUIStore } from '@/store';
import { useBreakpoint, useMediaQuery } from '@/hooks';
import { StyleConfigPanel } from '@/components/theme';
import { ROUTES } from '@/routes/routes';
import { identifyHelpScout } from '@/utils/helpScout';
import { LanguageSelector } from './LanguageSelector';
import { UserMenuDropdown } from './UserMenuDropdown';
import { HelpScoutButton } from './HelpScoutButton';
import motrAiIcon from '@/assets/images/icons/mot-r_AI.svg';

export const Header = () => {
  const { user, logout, hasPermission, hasFeature, featuresLoaded } = useAuth();
  const { data: unreadCount } = useUnreadCount();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { settings, companyName, isLoading: isTenantLoading } = useTenant();
  const { data: brandingLogos, isLoading: isBrandingLoading } = useBrandingLogos();
  const navigate = useNavigate();
  const { toggleMobileMenu, toggleSidebar, sidebarCollapsed } = useUIStore();
  const { isMobile, isTablet } = useBreakpoint();
  const [isThemePanelOpen, setIsThemePanelOpen] = useState(false);

  // Additional breakpoint for small desktop/large tablet.
  // Matches Sidebar's auto-collapse range (1024-1279) so the hamburger opens
  // the expanded menu as an overlay instead of uselessly toggling a state
  // that's overridden by the auto-collapse.
  const isSmallDesktop = useMediaQuery('(min-width: 1024px) and (max-width: 1279px)');
  const isCompact = isMobile || isTablet || isSmallDesktop;

  // Check if user can access theme settings (Admin role check)
  const canAccessThemeSettings = hasPermission('motr.Application.CSSOverride') || hasPermission('AbpIdentity.Roles');

  // Check if AI capabilities feature is enabled AND user has the permission
  const showAiAssistant = featuresLoaded && hasFeature('motr.PremiumFeatures.AiCapabilities.Enable') && hasPermission('motr.AskAssistant');

  // Construct user display name with proper fallbacks
  // Priority: name > (given_name + family_name) > preferred_username > 'User'
  const constructedFullName = user?.profile?.given_name || user?.profile?.family_name
    ? `${user?.profile?.given_name || ''} ${user?.profile?.family_name || ''}`.trim()
    : null;
  const userName = user?.profile?.name || constructedFullName || user?.profile?.preferred_username || t('User');
  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  // Identify the user to Help Scout Beacon once the user profile is loaded
  const userEmail = user?.profile?.email;
  useEffect(() => {
    if (userEmail) {
      identifyHelpScout({ name: userName, email: userEmail });
    }
  }, [userEmail, userName]);

  // Fetch profile picture and avatar color for current user
  const userId = user?.profile?.sub || '';
  const { data: pictureData } = useProfilePicture(userId);
  const { data: savedAvatarColor } = useMyAvatarColor();
  const pictureType = pictureData?.type;
  // Only compute image URL when type is not 0 (default/initials)
  const profilePictureUrl = pictureType !== undefined && pictureType !== 0
    ? (pictureData?.source || (pictureData?.fileContent ? `data:image/png;base64,${pictureData.fileContent}` : undefined))
    : undefined;
  // Avatar color: use saved color or default to brandPrimary
  const avatarColor = savedAvatarColor || undefined;

  const displayName = companyName || 'mot-r';

  // Show logo in header only when sidebar is collapsed/hidden:
  // - Mobile/tablet: sidebar is overlay (not visible by default)
  // - Small desktop: sidebar auto-collapses
  // - Desktop with manually collapsed sidebar
  const showHeaderLogo = isMobile || isTablet || isSmallDesktop || sidebarCollapsed;

  // Handle menu toggle based on screen size.
  // Small desktop uses the overlay path because the sidebar is force-collapsed
  // in that range — toggleSidebar would have no visible effect.
  const handleMenuToggle = () => {
    if (isMobile || isTablet || isSmallDesktop) {
      toggleMobileMenu();
    } else {
      toggleSidebar();
    }
  };

  // Impersonation state
  const impersonating = isImpersonating();
  const impersonationType = impersonating ? getImpersonationType() : null;
  const impersonatedTenantName = impersonating && impersonationType === 'tenant' ? getImpersonatedTenantName() : null;
  const impersonatedUserName = impersonating && impersonationType === 'user' ? getImpersonatedUserName() : null;
  const delegatedUserName = impersonating && impersonationType === 'delegation' ? getDelegatedUserName() : null;

  const handleEndImpersonation = () => {
    endImpersonation();
  };

  return (
    <>
      {/* Impersonation Banner */}
      {impersonating && (
        <div
          style={{
            backgroundColor: theme.colors.warning,
            color: theme.colors.textPrimary,
            padding: isMobile ? `${theme.spacing.sm} ${theme.spacing.md}` : `${theme.spacing.sm} ${theme.spacing.xxl}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing.md,
            position: 'sticky',
            top: 0,
            zIndex: 101,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, flex: 1 }}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span style={{ fontSize: theme.typography.fontSizeSm, fontWeight: theme.typography.fontWeightMedium }}>
              {impersonationType === 'delegation'
                ? <>{t('AuthorityDelegation:ActingAs')}: <strong>{delegatedUserName || t('Unknown')}</strong></>
                : impersonationType === 'user'
                  ? <>{t('Impersonating user')}: <strong>{impersonatedUserName || t('Unknown')}</strong></>
                  : <>{t('Impersonating tenant')}: <strong>{impersonatedTenantName || t('Unknown')}</strong></>
              }
            </span>
          </div>
          <button
            onClick={handleEndImpersonation}
            style={{
              padding: `${theme.spacing.md} ${theme.spacing.lg}`,
              backgroundColor: theme.colors.overlayWhite,
              border: 'none',
              borderRadius: theme.borderRadius.md,
              color: theme.colors.textPrimary,
              fontSize: theme.typography.fontSizeSm,
              fontWeight: theme.typography.fontWeightMedium,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.md,
              whiteSpace: 'nowrap',
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {impersonationType === 'delegation'
              ? t('AuthorityDelegation:BackToMyAccount')
              : impersonationType === 'user'
                ? t('Back to My Account')
                : t('Back to Host')
            }
          </button>
        </div>
      )}
      <header
      style={{
        height: '56px',
        backgroundColor: theme.colors.backgroundCard,
        borderBottom: theme.borders.default,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: isMobile ? `0 ${theme.spacing.md}` : `0 ${theme.spacing.xxl}`,
        position: 'sticky',
        top: impersonating ? '36px' : 0,
        zIndex: 100,
      }}
    >
      {/* Left side - Menu toggle and Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
        {/* Hamburger menu button */}
        <button
          onClick={handleMenuToggle}
          style={{
            background: 'none',
            border: 'none',
            padding: theme.spacing.sm,
            cursor: 'pointer',
            color: theme.colors.textSecondary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title={isMobile || isTablet || isSmallDesktop ? t('Menu:ToggleMenu') : (sidebarCollapsed ? t('Menu:ExpandMenu') : t('Menu:CollapseMenu'))}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        {/* Logo - only show when sidebar is collapsed/hidden to avoid duplication */}
        {showHeaderLogo && (() => {
          // Header is on light background: prefer dark logo (original colors), fall back to light
          const darkLogo = theme.sidebar.logoDarkUrl || brandingLogos?.darkLogoUrl || settings.logoDarkUrl;
          const lightLogo = theme.sidebar.logoLightUrl || brandingLogos?.lightLogoUrl || settings.logoLightUrl;
          if (darkLogo || lightLogo) {
            const logoSrc = darkLogo || lightLogo;
            return <img src={logoSrc} alt={displayName} style={{ height: '32px', maxWidth: isMobile ? '80px' : '120px', objectFit: 'contain' }} />;
          }
          // While branding logos are still loading, show placeholder instead of blob storage fallback
          if (isBrandingLoading || isTenantLoading) {
            return <span style={{ height: '32px', display: 'inline-block' }} />;
          }
          if (settings.logoUrl) {
            return <img src={settings.logoUrl} alt={displayName} style={{ height: '32px', maxWidth: isMobile ? '80px' : '120px', objectFit: 'contain' }} />;
          }
          return (
            <span
              style={{
                fontSize: isMobile ? theme.typography.fontSizeLg : theme.typography.fontSizeXl,
                fontWeight: theme.typography.fontWeightSemibold,
                color: theme.colors.textPrimary,
              }}
            >
              {displayName}
            </span>
          );
        })()}
      </div>

      {/* Right side - User menu */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? theme.spacing.xs : isCompact ? theme.spacing.sm : theme.spacing.lg,
          flexShrink: 0,
        }}
      >
        {/* AI Assistant button - only show when feature is enabled */}
        {showAiAssistant && (
          <button
            onClick={() => navigate(ROUTES.AI.ASSISTANT)}
            style={{
              background: 'none',
              border: 'none',
              padding: theme.spacing.sm,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={t('Menu:AskAi')}
          >
            <img
              src={motrAiIcon}
              alt={t('Menu:AskAi')}
              style={{ width: '24px', height: '24px' }}
            />
          </button>
        )}

        {/* Language selector - hide on very small screens */}
        {!isMobile && <LanguageSelector compact={isCompact} />}

        {/* Theme settings button - only show for admins, hide on mobile */}
        {canAccessThemeSettings && !isMobile && (
          <button
            onClick={() => setIsThemePanelOpen(true)}
            style={{
              background: 'none',
              border: 'none',
              padding: theme.spacing.sm,
              cursor: 'pointer',
              color: theme.colors.textSecondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={t('Header:ThemeSettings')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.04-.23-.29-.38-.63-.38-1.02 0-.83.67-1.5 1.5-1.5H16c3.31 0 6-2.69 6-6 0-4.96-4.49-9-10-9z" />
              <circle cx="6.5" cy="11.5" r="1.5" fill="currentColor" />
              <circle cx="9.5" cy="7.5" r="1.5" fill="currentColor" />
              <circle cx="14.5" cy="7.5" r="1.5" fill="currentColor" />
              <circle cx="17.5" cy="11.5" r="1.5" fill="currentColor" />
            </svg>
          </button>
        )}

        {/* Help Scout Beacon */}
        <HelpScoutButton />

        {/* Messages badge */}
        <button
          onClick={() => navigate('/messages')}
          style={{
            position: 'relative',
            background: 'none',
            border: 'none',
            padding: theme.spacing.sm,
            cursor: 'pointer',
            color: theme.colors.textSecondary,
          }}
          title={t('Menu:Messages')}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          {unreadCount != null && unreadCount > 0 && (
            <span
              style={{
                position: 'absolute',
                top: '2px',
                right: '2px',
                backgroundColor: theme.colors.error,
                color: 'white',
                fontSize: '9px',
                fontWeight: theme.typography.fontWeightSemibold,
                minWidth: theme.spacing.lg,
                height: theme.spacing.lg,
                borderRadius: theme.spacing.sm,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: `0 ${theme.spacing.xs}`,
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* User avatar and menu */}
        <UserMenuDropdown userName={userName} initials={initials} profilePictureUrl={profilePictureUrl} avatarColor={avatarColor} onLogout={() => logout()} />
      </div>

      {/* Theme Configuration Panel */}
      <StyleConfigPanel isOpen={isThemePanelOpen} onClose={() => setIsThemePanelOpen(false)} />
    </header>
    </>
  );
};
